import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import TrendCard from './TrendCard';
import Header from './Header';
import { useLaboratoryData } from '../hooks/useLaboratoryData';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { data: laboratoryData, loading, error, refetch } = useLaboratoryData();
  
  // Filter and date range state
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    endDate: new Date().toISOString().split('T')[0] // today
  });

  // Get unique categories and priorities for filter options
  const categories = useMemo(() => {
    if (!laboratoryData) return [];
    const uniqueCategories = Array.from(new Set(laboratoryData.map(item => item.category)));
    return uniqueCategories;
  }, [laboratoryData]);

  const priorities = useMemo(() => {
    if (!laboratoryData) return [];
    const uniquePriorities = Array.from(new Set(laboratoryData.map(item => item.priority)));
    return uniquePriorities;
  }, [laboratoryData]);

  // Filter the data based on selected filters
  const filteredData = useMemo(() => {
    if (!laboratoryData) return [];
    
    return laboratoryData.filter(item => {
      const categoryMatch = selectedCategory === 'all' || item.category === selectedCategory;
      const priorityMatch = selectedPriority === 'all' || item.priority === selectedPriority;
      return categoryMatch && priorityMatch;
    });
  }, [laboratoryData, selectedCategory, selectedPriority]);

  // Reset filters
  const resetFilters = () => {
    setSelectedCategory('all');
    setSelectedPriority('all');
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="dashboard">
      <Header 
        showFilterButton={true}
        // Filter props
        categories={categories}
        priorities={priorities}
        selectedCategory={selectedCategory}
        selectedPriority={selectedPriority}
        onCategoryChange={setSelectedCategory}
        onPriorityChange={setSelectedPriority}
        onResetFilters={resetFilters}
        filteredCount={filteredData.length}
        totalCount={laboratoryData?.length || 0}
        // Date range props
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
      />
      
      <div className="dashboard-content">
        <div className="dashboard-header">
          <h1>CLIA Laboratory Analytics</h1>
          <p>Comprehensive cytology/pathology laboratory compliance and quality metrics</p>
        </div>

        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading laboratory data...</p>
          </div>
        )}

        {error && (
          <div className="error-container">
            <div className="error-message">
              <h3>Error Loading Data</h3>
              <p>{error}</p>
              <button onClick={refetch} className="retry-button">
                Retry
              </button>
            </div>
          </div>
        )}

        {!loading && !error && filteredData && (
          <motion.div 
            className="trends-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {filteredData.map((data) => (
              <motion.div
                key={data.id}
                variants={cardVariants}
                whileHover={{ 
                  scale: 1.02,
                  transition: { duration: 0.2 }
                }}
                whileTap={{ scale: 0.98 }}
              >
                <TrendCard 
                  data={data} 
                  onClick={() => navigate(`/report/${data.id}`)}
                />
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Clinical Squared Branding */}
        <div className="dashboard-footer">
          <div className="footer-content">
            <div className="footer-left">
              <p className="footer-text">
                CLIA Laboratory Analytics Dashboard
              </p>
              <p className="footer-subtext">
                Comprehensive cytology/pathology laboratory compliance and quality metrics
              </p>
            </div>
            <div className="footer-right">
              <div className="clinical-squared-branding">
                <p className="powered-by">
                  Powered by{' '}
                  <a 
                    href="https://clinicalsquared.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="clinical-squared-link"
                  >
                    Clinical Squared
                  </a>
                  <span className="trademark">®</span>
                </p>
                <p className="company-tagline">
                  Healthcare IT Solutions & Data Analytics
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
